/** The process catalogue and the item route.
 *
 * A stage is the state an item is in after an operation, so stages belong to
 * the item's route — FKMT's own operations, named by FKMT. There is no global
 * list to agree and nothing to configure before starting.
 *
 * Most items have no route at all. They hold a single stage, `STOCK`, and
 * nothing about them changes. An item acquires stages the day somebody
 * defines an operation for it.
 *
 * JOB_WORK.md §4 and §8.1.
 */

import type { MasterSpec } from '@/types'

export const PROCESSES: MasterSpec = {
  module: 'masters', object: 'process', title: 'Processes',
  singular: 'Process',
  path: 'masters/processes', idKey: 'process_code',
  idGenerated: true,
  idGeneratedHelp: 'Issued by the system — nobody refers to a process by number',
  searchPlaceholder: 'Search processes…',
  // No Code column. The code is issued by the system and means nothing to
  // anybody — showing it in the list is the same clutter as asking for it on
  // the form, one step later.
  columns: [
    { key: 'name', label: 'Name' },
    { key: 'kind', label: 'Kind', width: '170px' },
    { key: 'result_stage', label: 'Leaves Material', width: '170px' },
  ],
  fields: [
    { key: 'name', label: 'Name', required: true },
    { key: 'kind', label: 'Kind', type: 'select', required: true,
      options: [
        { value: 'Operation', label: 'Operation — changes the material' },
        { value: 'ReturnableDelivery',
          label: 'Returnable delivery — sent out and returned unchanged' },
      ],
      help: 'Only Operations appear on an item route' },
    // Defined ONCE, here. A route then never asks, and cannot contradict it —
    // machining could not be made to produce "bevelled".
    { key: 'result_stage', label: 'Leaves Material', 
      optionsFrom: { path: 'masters/stages', value: 'stage_code', label: 'name' },
      help: 'The state this operation leaves material in. Blank for a ' +
            'returnable delivery, which changes nothing' },
  ],
}

/** One row per operation. The INPUT stage is not a field — it is the result
 *  stage of the operation before it, and `STOCK` for the first. Storing both
 *  would let them disagree, which is the same failure as a stored stock
 *  balance at a smaller scale. */
export const ITEM_ROUTES: MasterSpec = {
  module: 'masters', object: 'item_route', title: 'Item Routes',
  singular: 'Operation',
  path: 'masters/item-routes', idKey: 'op_seq',
  searchPlaceholder: 'Search by item or stage…',
  columns: [
    { key: 'item_id', label: 'Item', width: '120px' },
    { key: 'op_seq', label: 'Op', width: '70px', numeric: true },
    { key: 'process_code', label: 'Process', width: '120px' },
    { key: 'is_job_work', label: 'Job Work', width: '110px' },
    { key: 'description', label: 'Description' },
  ],
  fields: [
    { key: 'op_seq', label: 'Operation No.', type: 'number', required: true,
      immutable: true,
      help: 'Order of the operation — 10, 20, 30 leaves room to insert later' },
    // Only items FKMT has marked as needing a route. The system cannot
    // infer which those are — a bought-out casting may need machining and a
    // fabricated bracket may not — so the item master says so, and this list
    // stays short instead of holding the whole catalogue.
    { key: 'item_id', label: 'Item', required: true,
      optionsFrom: { path: 'masters/items', value: 'item_id',
                     label: 'item_name', query: { needs_route: 'true' } },
      help: 'Only items marked “Has operations” on the item master appear here' },
    // Operations only. A returnable delivery — material sent out for
    // inspection and returned unchanged — is not an item operation and does
    // not belong on a route.
    { key: 'process_code', label: 'Process', required: true,
      optionsFrom: { path: 'masters/processes', value: 'process_code',
                     label: 'name', query: { kind: 'Operation' } } },
    { key: 'is_job_work', label: 'Sent Outside', type: 'select',
      options: [
        { value: 'false', label: 'No — done in-house' },
        { value: 'true', label: 'Yes — third-party job work' },
      ],
      help: 'An in-house operation still moves the stage; only an outside ' +
            'one raises a job work order' },
    { key: 'description', label: 'Description', type: 'textarea' },
  ],
}

/** The states an item can be in.
 *
 * Small and picked, never typed. Stock is held and valued per item +
 * location + stage, so a misspelt stage is not an untidy label — it is stock
 * that exists, holds value, and can never be found again.
 */
export const STAGES: MasterSpec = {
  module: 'masters', object: 'stage', title: 'Stages',
  singular: 'Stage',
  path: 'masters/stages', idKey: 'stage_code',
  searchPlaceholder: 'Search stages…',
  columns: [
    { key: 'stage_code', label: 'Code', width: '160px' },
    { key: 'name', label: 'Name' },
  ],
  fields: [
    { key: 'stage_code', label: 'Code', required: true, immutable: true,
      help: 'Capitals, digits and underscore — MACHINED, STRESS_RELIEVED' },
    { key: 'name', label: 'Name', required: true },
  ],
}
